'use client';

import { Button as CKButton } from '@1debit/chimekit';
import { ButtonWithIconProps } from '@1debit/chimekit/dist/server/Button/Button';
import { Sys, InternalPage } from 'libs/contentful/types';
import { cn } from 'utils/styles';
import { MouseEventHandler } from 'react';
import { PropsWithChildrenAndClass } from 'types/sharedComponents';
import Link from '../Link/Link';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { titleToCamel } from 'utils/formatters';
import { LinkTargets } from 'types/constants';

interface BaseProps extends PropsWithChildrenAndClass, Pick<ButtonWithIconProps, 'iconProps'> {
  __typename?: string;
  sys?: Sys;
  id?: string;
}

interface ButtonAsLinkProps {
  href?: string;
  internalPage?: InternalPage;
  target?: LinkTargets;
  onClick?: MouseEventHandler;
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  href?: never; //Typed as never so this can be destructed in wrapper component
  internalPage?: InternalPage;
  target?: LinkTargets;
}

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant: 'icon';
  label: string;
  href?: never;
  internalPage?: InternalPage;
  target?: LinkTargets;
}

export type ButtonVariant = 'solid' | 'solidInverse' | 'outline' | 'outlineScroll' | 'icon' | 'navMenu' | 'link';

export type ButtonCtaLevel = 'primary' | 'secondary';

export type StyledButtonProps = BaseProps &
  (ButtonAsLinkProps | ButtonProps | IconButtonProps) & {
    variant?: ButtonVariant;
    ctaLevel?: ButtonCtaLevel;
    testId?: string;
  };

// Wrapper Component that handles all the styling logic and determines whether to render link or button
const Button = ({
  className,
  variant = 'solid',
  ctaLevel = 'primary',
  iconProps,
  href,
  internalPage,
  target,
  sys,
  testId,
  ...restProps
}: StyledButtonProps) => {
  const formattedVariant = variant ? titleToCamel(variant) : '';

  const baseOutlineStyles = cn(
    'border-[1.5px] bg-transparent hover:bg-transparent active:bg-transparent text-current hover:text-current',
  );
  const noBorderStyles = 'border-transparent';
  const baseSolidStyles = cn(
    noBorderStyles,
    'text-(--btn-text) bg-(--btn-bg) hover:bg-(--web-btn-bg-hover) hover:text-(--web-btn-text-hover) text-wrap border-[1.5px] leading-tight min-w-40',
  );

  const buttonStyles = {
    // Solid button has a transparent border so the height matches the outline button height
    solid: baseSolidStyles,
    solidInverse: cn(
      baseSolidStyles,
      '[--btn-text:var(--color-content-inverse)] [--btn-bg:var(--color-kale)] [--web-btn-bg-hover:rgb(21,116,74)] [--web-btn-text-hover:var(--color-content-inverse)]',
    ),
    outline: cn(
      baseOutlineStyles,
      'border-(--btn-outline) hover:border-(--btn-outline-hover) active:border-(--btn-outline-hover) min-w-40 leading-tight',
    ),
    // note: py-6.5 subtracts the 1.5px border to equal 8px padding.
    outlineScroll: cn(
      baseOutlineStyles,
      'rounded-[70px] border-(--btn-outline-scroll) hover:bg-(--btn-outline-scroll) active:bg-(--btn-outline-scroll) hover:text-(--btn-outline-scroll-hover) active:text-(--btn-outline-scroll-hover) py-[6.5px] leading-[19px]',
    ),
    icon: cn(baseOutlineStyles, noBorderStyles, 'px-2.5'),
    navMenu: cn(baseOutlineStyles, 'text-kale text-left items-start justify-start p-0 border-0'),
    link: cn(
      'leading-[1.4] underline hover:decoration-transparent transition duration-300 p-0 bg-transparent hover:bg-transparent',
    ),
  };

  // Type guard to ensure the variant is valid after formatting
  const isValidVariant = (variant: string): variant is keyof typeof buttonStyles => {
    return variant in buttonStyles;
  };

  const styles = cn(
    'label-saans font-semibold rounded-md cursor-pointer w-full md:max-w-max py-2 px-4',
    isValidVariant(formattedVariant) ? buttonStyles[formattedVariant] : buttonStyles.solid,
    className,
  );

  const scrollIconProps = {
    position: 'trailing',
    icon: 'chevron_down',
  } as ButtonWithIconProps['iconProps'];
  const icon = formattedVariant === 'outlineScroll' ? scrollIconProps : iconProps;
  const sharedProps = { className: styles, ...restProps };
  const hasDestination = Boolean(href || internalPage);

  if (hasDestination) {
    // Extract only the props safe to pass to Link component
    const { onClick, children, className, id } = sharedProps;
    return (
      <Link
        id={id}
        asButton={formattedVariant !== 'link'}
        withCtaAnalytics
        href={href}
        internalPage={internalPage}
        target={target}
        iconProps={icon}
        sys={sys}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
        className={className}
        ctaLevel={ctaLevel}
        data-testid={testId}
        {...liveUpdateAttrs(sys?.id, 'text')}>
        {children}
      </Link>
    );
  }

  if (formattedVariant === 'icon') {
    const { label, children } = sharedProps as IconButtonProps;
    return (
      <CKButton analyticsId={null} {...sharedProps}>
        {children}
        <span className="sr-only">{label}</span>
      </CKButton>
    );
  }

  return <CKButton analyticsId={null} iconProps={icon} {...sharedProps} />;
};

export default Button;
